import React, { useState } from 'react';
import axios from 'axios';
import { X, Minus, Plus, Trash2, Truck, Tag, Loader2, Check } from 'lucide-react';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { startStripeCheckout, extractBundleId } from '../lib/checkout';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STRIPE_MIN = { USD: 0.5, CAD: 0.5, EUR: 0.5, GBP: 0.3 };

const CartDrawer = () => {
  const { isOpen, closeCart, items, updateQty, removeItem } = useCart();
  const { formatPrice, currency, bundles } = useCurrency();
  const fp = formatPrice || ((v) => `$${Number(v).toFixed(2)}`);

  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // {code, percent_off, label}
  const [validating, setValidating] = useState(false);

  // Compute LIVE price/subtotal from current-currency bundles
  const liveItems = items.map((it) => {
    const bundleId = extractBundleId(it.id);
    const live = bundles?.find((b) => b.id === bundleId);
    const livePrice = live ? Number(live.price) : Number(it.price);
    return { ...it, livePrice, lineTotal: livePrice * it.qty };
  });
  const subtotal = liveItems.reduce((sum, it) => sum + it.lineTotal, 0);

  // Discount preview (server still has final word)
  const discountAmount = coupon ? Number((subtotal * coupon.percent_off / 100).toFixed(2)) : 0;
  const floor = STRIPE_MIN[currency] ?? 0.5;
  const totalAfter = coupon
    ? Math.max(Number((subtotal - discountAmount).toFixed(2)), floor)
    : subtotal;

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setValidating(true);
    try {
      const { data } = await axios.post(`${API}/coupon/validate`, { code });
      setCoupon(data);
      toast.success(`Coupon applied: ${data.percent_off}% off`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid coupon';
      toast.error(msg);
      setCoupon(null);
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput('');
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[80] transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
        onClick={closeCart}
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] z-[90] flex flex-col transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: '#F5EDDF' }}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <span className="font-serif italic text-[26px]" style={{ color: '#2D5A3D' }}>K-Root</span>
          <button onClick={closeCart} aria-label="Close" style={{ color: '#2D5A3D' }}>
            <X size={22} />
          </button>
        </div>

        <div className="mx-6 mb-4 rounded-xl px-3 py-2.5 flex items-center gap-2"
          style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}>
          <Truck size={16} />
          <p className="text-[12px] font-semibold tracking-wide">
            ✓ Free Shipping Worldwide — No Hidden Fees
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 scrollbar-thin">
          {items.length === 0 ? (
            <div className="py-16 text-center">
              <h3 className="font-serif text-[26px]" style={{ color: '#2D5A3D' }}>Your cart is empty</h3>
              <button
                onClick={closeCart}
                className="mt-6 inline-block px-7 py-3 text-[13px] uppercase tracking-[0.18em] rounded-full btn-hover"
                style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'rgba(45,90,61,0.12)' }}>
              {liveItems.map((it) => (
                <li key={it.id} className="py-4 flex gap-3" style={{ borderColor: 'rgba(45,90,61,0.12)' }}>
                  <img src={it.image} alt={it.title} className="w-20 h-20 object-cover rounded-lg bg-white" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium leading-snug" style={{ color: '#2D5A3D' }}>
                      {it.title}
                    </p>
                    {it.variant && (
                      <p className="text-[12px] opacity-70 mt-0.5" style={{ color: '#2D5A3D' }}>{it.variant}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="inline-flex items-center border rounded-lg" style={{ borderColor: 'rgba(45,90,61,0.3)' }}>
                        <button onClick={() => updateQty(it.id, it.qty - 1)} className="w-7 h-7 flex items-center justify-center" style={{ color: '#2D5A3D' }} aria-label="Decrease">
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center text-[13px]" style={{ color: '#2D5A3D' }}>{it.qty}</span>
                        <button onClick={() => updateQty(it.id, it.qty + 1)} className="w-7 h-7 flex items-center justify-center" style={{ color: '#2D5A3D' }} aria-label="Increase">
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[14px] font-medium" style={{ color: '#2D5A3D' }}>
                          {fp(it.lineTotal)}
                        </span>
                        <button onClick={() => removeItem(it.id)} aria-label="Remove" style={{ color: '#2D5A3D' }} className="opacity-70 hover:opacity-100">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-6 py-5 border-t" style={{ borderColor: 'rgba(45,90,61,0.15)' }}>
            {/* Coupon */}
            <div className="mb-4">
              {coupon ? (
                <div
                  className="flex items-center justify-between px-3 py-2 rounded-lg"
                  style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
                >
                  <span className="flex items-center gap-2 text-[12px] font-semibold">
                    <Check size={14} /> {coupon.code} — {coupon.percent_off}% OFF
                  </span>
                  <button onClick={removeCoupon} aria-label="Remove coupon" className="opacity-80 hover:opacity-100">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 flex items-center gap-2 px-3 rounded-lg border" style={{ borderColor: 'rgba(45,90,61,0.3)', backgroundColor: '#FFFFFF' }}>
                    <Tag size={14} color="#2D5A3D" />
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(); }}
                      placeholder="Discount code"
                      className="flex-1 bg-transparent py-2 text-[13px] focus:outline-none uppercase tracking-wide"
                      style={{ color: '#2C2418' }}
                    />
                  </div>
                  <button
                    onClick={applyCoupon}
                    disabled={!couponInput.trim() || validating}
                    className="px-4 rounded-lg text-[12px] uppercase tracking-wide font-semibold btn-hover disabled:opacity-50"
                    style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
                  >
                    {validating ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-1">
              <span className="text-[14px] uppercase tracking-[0.15em]" style={{ color: '#2D5A3D' }}>Subtotal</span>
              <span className="text-[14px]" style={{ color: '#2D5A3D' }}>{fp(subtotal)}</span>
            </div>

            {coupon && (
              <div className="flex items-center justify-between mb-1">
                <span className="text-[13px]" style={{ color: '#2D5A3D' }}>
                  Discount ({coupon.percent_off}%)
                </span>
                <span className="text-[13px] font-semibold" style={{ color: '#2D5A3D' }}>
                  − {fp(discountAmount)}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between mb-3">
              <span className="text-[12px] flex items-center gap-1" style={{ color: '#2D5A3D' }}>
                <Truck size={13} /> Shipping
              </span>
              <span className="text-[12px] font-semibold" style={{ color: '#2D5A3D' }}>FREE</span>
            </div>

            <div className="flex items-center justify-between mb-3 pt-2 border-t" style={{ borderColor: 'rgba(45,90,61,0.15)' }}>
              <span className="text-[15px] font-bold uppercase tracking-[0.12em]" style={{ color: '#2D5A3D' }}>Total</span>
              <span className="text-[18px] font-bold" style={{ color: '#2D5A3D' }}>{fp(totalAfter)}</span>
            </div>

            <p className="text-[11px] opacity-70 mb-3" style={{ color: '#2D5A3D' }}>
              {currency === 'GBP' || currency === 'EUR'
                ? 'All prices include VAT. No additional fees at checkout.'
                : currency === 'CAD'
                ? 'GST/PST calculated at checkout.'
                : 'No additional fees at checkout.'}
            </p>
            <button
              onClick={() => {
                const first = items[0];
                if (!first) return;
                const bundle_id = extractBundleId(first.id);
                startStripeCheckout({
                  bundle_id,
                  quantity: first.qty,
                  currency,
                  coupon_code: coupon?.code,
                });
              }}
              className="w-full py-4 rounded-full text-[13px] uppercase tracking-[0.2em] font-medium btn-hover"
              style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
            >
              Checkout — Pay with Stripe
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
